import { History, ArrowRight } from 'lucide-react';
import type { TripTransition } from '../../types/trip';
import { TRIP_STATUS_COLORS, TRIP_STATUS_LABELS, formatDateTime } from '../../utils/trips';

/**
 * FE-11 — la novedad del viaje: append-only y auditada (PRODUCT.md §4.8).
 *
 * Lo que hace que valga la pena pintarla es la CORRECCIÓN: el "de qué a qué"
 * de la ventana es lo que reescribió el expediente entero sin tocar una sola
 * posición.
 *
 * ⚠️ Reconocerla por `fromWindow`/`toWindow` NO funciona, aunque el contrato
 * lo sugiera: medido contra el viaje real de `PAT001`, el backend manda las
 * dos ventanas también en `start` y en `finish` —que mueven la ventana, claro
 * que sí, pero son el viaje ocurriendo, no alguien reescribiendo el pasado—.
 * La marca fiable es que una corrección **no cambia de estado**: entra y sale
 * del mismo. Y el bloque antes/después solo se pinta si las dos ventanas
 * difieren de verdad; el backend las manda iguales cuando no movió nada.
 *
 * ⚠️ El orden llega mal del backend (issue #94); se corrige en el mapper, no
 * aquí. Este componente asume una lista ya cronológica.
 */
export function TripTimeline({ transitions }: { transitions: TripTransition[] }) {
  if (transitions.length === 0) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
        Sin movimientos registrados.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <History size={15} style={{ color: 'var(--accent)' }} />
        Novedad del viaje
      </h4>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {transitions.map((t, i) => {
          const color = TRIP_STATUS_COLORS[t.toStatus];
          const esCorreccion = !!t.fromStatus && t.fromStatus === t.toStatus;
          const ventanaCambio = !!t.fromWindow && !!t.toWindow &&
            (t.fromWindow.from !== t.toWindow.from || t.fromWindow.to !== t.toWindow.to);
          return (
            <li
              key={`${t.occurredAt}-${i}`}
              style={{
                display: 'flex',
                gap: '0.75rem',
                background: 'var(--bg-raised)',
                border: '1px solid var(--border)',
                borderLeft: `3px solid ${color}`,
                borderRadius: 'var(--radius-md)',
                padding: '0.625rem 0.875rem',
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {t.fromStatus && (
                    <>
                      <span style={{ color: 'var(--text-muted)' }}>{TRIP_STATUS_LABELS[t.fromStatus]}</span>
                      <ArrowRight size={12} style={{ color: 'var(--text-muted)' }} />
                    </>
                  )}
                  <span style={{ color }}>{TRIP_STATUS_LABELS[t.toStatus]}</span>
                  {esCorreccion && (
                    <span style={{ fontSize: '10px', fontWeight: 800, color: '#F59E0B', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: '99px', padding: '1px 7px' }}>
                      CORRECCIÓN
                    </span>
                  )}
                </div>

                {ventanaCambio && (
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span>Antes: {formatDateTime(t.fromWindow!.from)} → {formatDateTime(t.fromWindow!.to)}</span>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                      Ahora: {formatDateTime(t.toWindow!.from)} → {formatDateTime(t.toWindow!.to)}
                    </span>
                  </div>
                )}

                {t.reason && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>“{t.reason}”</p>
                )}

                {/* El contrato entrega el `actor` como userId crudo y no hay
                    endpoint que lo resuelva sin pedir permisos de admin. Se
                    muestra abreviado con el valor completo en el tooltip —
                    resolverlo a nombre queda anotado en PENDIENTES.md. */}
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }} title={t.actor}>
                  {formatDateTime(t.occurredAt)} · {t.actor === 'system' ? 'automático' : `usuario ${t.actor.slice(0, 8)}`}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
